import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  QueryList
} from '@angular/core';
import {CarouselItemDirective} from './carousel-item/carousel-item.directive';
import {CommonModule} from '@angular/common';
import {FabComponent} from '@aviary/buttons/fab/fab.component';

type ScrollButtonDirection = 'REVERSE' | 'FORWARD';

@Component({
  selector: 'mx-carousel',
  host: {
    class: 'flex flex-row place-content-start gap-x-5 w-full overflow-x-hidden'
  },
  templateUrl: './carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FabComponent]
})
export class CarouselComponent implements AfterViewInit, OnDestroy {

  @ContentChildren(CarouselItemDirective)
  public set items(items: QueryList<CarouselItemDirective>) {
    this.unobserveItemElements();

    this.itemElements = items.toArray().map(item => item.element);
    this.hasItems = items.length > 0;
    this.largestItemIndex = items.length - 1;

    this.observeItemElements(); // If this setter is called before the view is initialized, this won't do anything.  But then ngAfterViewInit() handles it.
  }

  @Input() public noItemsMessage: string = 'No items found.';
  @Input() public buttonClasses: string = '';
  @Input() public buttonIconClasses: string = '';
  @Input() public buttonIconSize: number = 10;
  @Input() public extendButtons: boolean = true;

  public hasItems: boolean = false;
  public showReverseButton: boolean = false;
  public showForwardButton: boolean = false;

  private readonly hostElement: HTMLElement = inject(ElementRef).nativeElement;
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private intersectionObserver?: IntersectionObserver;
  private itemElements: HTMLElement[] = [];
  private largestItemIndex: number;
  private fullyVisibleIndices: Set<number> = new Set();
  private smallestFullyVisibleItemIndex: number;
  private largestFullyVisibleItemIndex: number;
  private reverseButtonTargetItemIndex: number;
  private forwardButtonTargetItemIndex: number;

  public ngAfterViewInit(): void {
    this.initializeIntersectionObserver();
    this.observeItemElements();  // If there are already items, we weren't observing them yet.  If not, the items() setter will call this again when there are.
  }

  public ngOnDestroy(): void {
    this.unobserveItemElements();
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;  // Just in case an item list update ever sneaks in on our way out (somehow?), this will make us ignore it.
  }

  @HostListener('document:visibilitychange')
  public onDocumentVisibilityChange(): void {
    /*
      Chrome's implementation of IntersectionObserver doesn't emit events on inactive tabs ("inactive" being when a different tab is active, or when the whole
      browser is minimized), even though Firefox's implementation does.  (https://issues.chromium.org/issues/41383759)

      So if a tab was loaded with a carousel present, we wouldn't get the expected element intersection events to tell us whether to show/hide our directional
      buttons.  So we'd be in a bad state once the tab did become visible.

      Thus, we tie our element observers' lifetimes to the document's current visibility status, which ensures that we restart any observations whenever the
      document becomes visible.  Cancelling the observers here probably doesn't have much benefit -- we might save some browser resources, but there (probably?)
      wouldn't be any intersection changes on an inaccessible tab anyway -- but we'd have to unobserve before re-observing when we get a 'visible' event anyway.
    */
    if (document.visibilityState === 'hidden') {
      this.unobserveItemElements();
    } else {
      this.observeItemElements();
    }
  }

  public onClickButton(buttonDirection: ScrollButtonDirection): void {
    if (buttonDirection === 'REVERSE') {
      this.itemElements[this.reverseButtonTargetItemIndex]?.scrollIntoView({inline: 'end', block: 'nearest', behavior: 'smooth'});
    } else {
      this.itemElements[this.forwardButtonTargetItemIndex]?.scrollIntoView({inline: 'start', block: 'nearest', behavior: 'smooth'});
    }
  }

  private initializeIntersectionObserver(): void {
    this.intersectionObserver =
      new IntersectionObserver(
        this.onIntersectionObserverUpdate.bind(this),
        {
          root: this.hostElement,
          rootMargin: '0px 1px 0px 0px', // Without this 1px right margin, the observer doesn't properly sense when the rightmost item is fully visible.
          threshold: [1]                 // Report "isIntersecting" only when the entry's element has transitioned to/from 100% visible inside the viewport.
        }
      );
  }

  private unobserveItemElements(): void {
    if (!this.intersectionObserver) return; // Might get here before the view is fully initialized, which we know if the observer hasn't been instantiated yet.

    for (const element of this.itemElements) {
      this.intersectionObserver.unobserve(element);  // We might be un-observing an element that hasn't yet been observed, but that's a safe no-op.
    }
  }

  private observeItemElements(): void {
    if (!this.intersectionObserver) return; // Might get here before the view is fully initialized, which we know if the observer hasn't been instantiated yet.
    if (document.visibilityState === 'hidden') return; // See onDocumentVisibilityChange().

    for (const [index, element] of Object.entries(this.itemElements)) {
      element.dataset['carouselIndex'] = index;  // When the observer calls us, we get individual elements, not array indices.  So attach the index here.
      this.intersectionObserver.observe(element);
    }
  }

  private onIntersectionObserverUpdate(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const carouselIndex: number = Number((entry.target as HTMLElement).dataset['carouselIndex']);

      // The observer calls us only with elements that have changed their "isIntersecting" value, so we can just add/remove them to/from our map accordingly.
      this.fullyVisibleIndices[entry.isIntersecting ? 'add' : 'delete'](carouselIndex);
    }

    this.calculateVisibleIndexExtremes();
    this.updateButtonVisibilities();
    this.setButtonTargetItemIndices();
    this.changeDetector.markForCheck();
  }

  private calculateVisibleIndexExtremes(): void {
    const fullyVisibleIndicesAsArray: number[] = Array.from(this.fullyVisibleIndices);
    this.smallestFullyVisibleItemIndex = Math.min(...fullyVisibleIndicesAsArray);
    this.largestFullyVisibleItemIndex = Math.max(...fullyVisibleIndicesAsArray);
  }

  private updateButtonVisibilities(): void {
    if (isFinite(this.smallestFullyVisibleItemIndex) && isFinite(this.largestFullyVisibleItemIndex)) {
      // compare our currently visible indices to the absolute smallest/largest indices.
      this.showReverseButton = this.smallestFullyVisibleItemIndex > 0;
      this.showForwardButton = this.largestFullyVisibleItemIndex < this.largestItemIndex;
    } else {
      // it's common to get infinity and -infinity if the fullyVisibleIndices is an empty set
      this.showReverseButton = false;
      this.showForwardButton = false;
    }
  }

  private setButtonTargetItemIndices(): void {
    /*
      Set our targets to just before/after the first/last FULLY visible element.  (This means the target could be partially visible now, and it will also end up
      being our first/last fully visible element after the scroll button click.)  Out-of-range values don't matter here; we account for those elsewhere.
    */
    this.reverseButtonTargetItemIndex = this.smallestFullyVisibleItemIndex - 1;
    this.forwardButtonTargetItemIndex = this.largestFullyVisibleItemIndex + 1;
  }

  public get leftButtonOffsetClass(): string {
    return this.extendButtons ? '-left-6' : 'left-0';
  }

  public get rightButtonOffsetClass(): string {
    return this.extendButtons ? '-right-6' : 'right-0';
  }
}
